/**
 * FormCardsHead — cabecera estándar de las fichas en variante «tarjetas».
 *
 * Repite siempre la misma estructura (ver `docs/FICHA_ESTANDAR_TARJETAS.md`):
 *   icono · título editable con ✏️/✓/✗ · miga · badges (derecha) · chips
 *
 * Se usa dentro de `FormCardsShell`:
 *
 *   <FormCardsShell
 *     isCards={isCards}
 *     header={isCards ? (
 *       <FormCardsHead
 *         icon={<KeyIcon />}
 *         title={form.product}
 *         onTitleChange={(v) => sf('product', v)}
 *         editing={editing}
 *         saving={saving}
 *         onSave={handleSave}
 *         onCancel={handleCancelEdit}
 *         onEdit={() => setEditing(true)}
 *         placeholder="Nombre…"
 *         crumb="proveedor › contrato"
 *         badges={[{ text: 'Activa', color: '#16a34a' }, { text: 'Perpetua' }]}
 *         chips={[{ value: 3, label: 'equipos' }]}
 *       />
 *     ) : (<input className="dv-record-title" … />)}
 *   >
 *
 * En la ficha clásica NO se usa: cada formulario conserva su título de siempre.
 */
import React from 'react';
import { CheckIcon, XMarkIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

export default function FormCardsHead({
  icon = null,
  title = '',
  onTitleChange,
  placeholder = 'Nombre…',
  editing = false,
  saving = false,
  onSave,
  onCancel,
  onEdit,
  disabled = false,
  crumb = '',
  badges = [],
  chips = [],
  extra = null,
  // Modo «título en línea» (el de la ficha canónica del rack). Si no se pasa
  // `onTitleCommit`, la cabecera se comporta como siempre: el título solo se
  // edita entrando en edición con el lápiz.
  dirty = true,
  titleDirty = false,
  onTitleCommit,
  onTitleReset,
}) {
  const inline = typeof onTitleCommit === 'function';
  const showActions = inline ? (editing || titleDirty) : editing;
  const canEditTitle = !disabled && (inline || editing);
  const pencilClass = inline ? 'sf2-title-btn sf2-title-pencil' : 'sf2-title-btn';

  const handleKeyDown = (event) => {
    if (!inline) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.blur();
      onTitleCommit?.();
    } else if (event.key === 'Escape') {
      /* Esc restaura el título y NO cierra la ficha: hay fichas con un atajo
         global de Escape que, sin esto, cerraba la pantalla entera. */
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.blur();
      onTitleReset?.();
    }
  };

  return (
    <>
      <div className="sf2-head">
        <div className="sf2-head-icon" aria-hidden="true">{icon}</div>
        <div className="sf2-head-title">
          <div className="sf2-title-row">
            <input
              className="sf2-head-input"
              value={title}
              onChange={(event) => onTitleChange?.(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              aria-label="Nombre del registro"
              style={{ '--sf2-title-len': (title || '').length }}
              disabled={!canEditTitle}
            />
            {showActions ? (
              <>
                <button
                  type="button"
                  className="sf2-title-btn sf2-title-btn--ok"
                  onClick={onSave}
                  disabled={saving || (inline && !dirty)}
                  title="Guardar cambios"
                >
                  {saving ? <span className="dv-spinner" /> : <CheckIcon />}
                </button>
                <button
                  type="button"
                  className="sf2-title-btn"
                  onClick={onCancel}
                  disabled={saving}
                  title={inline ? 'Descartar cambios' : 'Cancelar la edición y descartar cambios'}
                >
                  <XMarkIcon />
                </button>
              </>
            ) : (
              <button
                type="button"
                className={pencilClass}
                onClick={(event) => (inline
                  ? event.currentTarget.parentElement.querySelector('.sf2-head-input')?.focus()
                  : onEdit?.())}
                title="Editar la ficha"
                aria-label="Editar la ficha"
              >
                <PencilSquareIcon />
              </button>
            )}
          </div>
          {crumb ? <div className="sf2-crumb">{crumb}</div> : null}
        </div>
        {badges.length > 0 && (
          <div className="sf2-badges">
            {badges.map((b, i) => {
              /* `tone` pinta la píldora con la paleta de estado de la ficha
                 canónica; `color` (sin `tone`) sigue siendo solo el punto. */
              const toneStyles = {
                ok:   { background: '#dcfce7', color: '#15803d', borderColor: 'transparent', dot: '#15803d' },
                bad:  { background: '#fee2e2', color: '#991b1b', borderColor: 'transparent', dot: '#991b1b' },
                warn: { background: '#fef9c3', color: '#854d0e', borderColor: 'transparent', dot: '#854d0e' },
              }[b.tone];
              return (
                <span
                  className="sf2-badge"
                  key={`${b.text}-${i}`}
                  style={toneStyles
                    ? { background: toneStyles.background, color: toneStyles.color, borderColor: toneStyles.borderColor }
                    : undefined}
                >
                  {(b.color || toneStyles) ? <i style={{ backgroundColor: toneStyles ? toneStyles.dot : b.color }} /> : null}
                  {b.text}
                </span>
              );
            })}
          </div>
        )}
      </div>
      {(chips.length > 0 || extra) && (
        <div className="sf2-chips">
          {chips.map((c, i) => (
            <span
              key={`${c.label}-${i}`}
              className={`sf2-chip${c.flat === false ? '' : ' sf2-chip--flat'}`}
              role={c.onClick ? 'button' : undefined}
              tabIndex={c.onClick ? 0 : undefined}
              style={c.onClick ? { cursor: 'pointer' } : undefined}
              onClick={c.onClick}
              onKeyDown={c.onClick
                ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); c.onClick(); } }
                : undefined}
            >
              <b>{c.value}</b><span>{c.label}</span>
            </span>
          ))}
          {extra}
        </div>
      )}
    </>
  );
}
